/**
 * installPrompt.js — global PWA install singleton.
 *
 * One module-level reference to the `beforeinstallprompt` event so both the
 * auto-popup (`<PWAInstallPrompt />`) and explicit "Install app" CTAs
 * (`<InstallAppLink />`) can share the same install state.
 *
 * On iOS Safari, no programmatic install API exists; consumers fall back to
 * the Share → Add to Home Screen instructions.
 */

let deferred = null;
const subs = new Set();

if (typeof window !== "undefined") {
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferred = e;
        subs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    });
    window.addEventListener("appinstalled", () => {
        deferred = null;
        subs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
    });
}

export function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator?.standalone === true
    );
}

export function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

export function hasDeferredInstall() {
    return !!deferred;
}

export function subscribe(fn) {
    subs.add(fn);
    return () => subs.delete(fn);
}

/**
 * Attempt to trigger the native install prompt.
 * Resolves to:
 *   "installed"   — user accepted (Chromium)
 *   "dismissed"   — user rejected (Chromium)
 *   "unavailable" — no deferred event (typical on iOS or already installed)
 */
export async function triggerInstall() {
    if (!deferred) return "unavailable";
    try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        deferred = null;
        subs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
        return choice?.outcome === "accepted" ? "installed" : "dismissed";
    } catch {
        return "unavailable";
    }
}
